import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import socketClient from '../../utils/socketClient';
import Navbar from '../Navigation/Navbar';
import SciFiBackground from '../SciFiBackground/SciFiBackground';
import Confetti from 'react-confetti';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import GavelIcon from '@mui/icons-material/Gavel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LoginIcon from '@mui/icons-material/Login';

const CURRENCY_SCALE = 100;

const LiveAuctionRoom = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user, token, refreshUser } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [countdownProgress, setCountdownProgress] = useState(100);
  const [latency, setLatency] = useState(null);
  const [socketStatus, setSocketStatus] = useState('disconnected');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [joinMode, setJoinMode] = useState('participant');
  const [, setObserverCount] = useState(0);
  const isGuest = !token;
  const isObserver = isGuest || joinMode === 'observer';
  const canBid = Boolean(token) && !isObserver;
  const [participationConfirmed, setParticipationConfirmed] = useState(false);
  const [showParticipationConfirm, setShowParticipationConfirm] = useState(false);
  const [participationStatusPending, setParticipationStatusPending] = useState(true);
  const [participationFeeReceipt, setParticipationFeeReceipt] = useState(0);
  const [needPack, setNeedPack] = useState(null); // { packIndex, price }
  const [isPurchasingPack, setIsPurchasingPack] = useState(false);
  const [autoPurchaseNext, setAutoPurchaseNext] = useState(false);
  const [packPricing, setPackPricing] = useState({ pricing: [], increment: null });
  const [packStatus, setPackStatus] = useState({
    packs: [],
    increment: 3,
    totalPacks: 0,
    totalBidsGranted: 0,
    totalBidsUsed: 0,
    currentPackIndex: null,
    bidsLeftInCurrent: 0,
    bidsLostValue: 0,
  });

  const [auctionRoom, setAuctionRoom] = useState(null);
  const [, setParticipants] = useState([]);
  const [, setParticipantCount] = useState(0);
  const [bidHistory, setBidHistory] = useState([]);
  const [userBidCount, setUserBidCount] = useState(0);
  const [isPlacingBid, setIsPlacingBid] = useState(false);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [auctionWinner, setAuctionWinner] = useState(null);
  const QUICK_BID_INCREMENT = 0.1;
  const TIME_ENDED = '__TIME_ENDED__';
  const roundToCurrency = useCallback((value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return 0;
    }
    return Math.round((numeric + Number.EPSILON) * CURRENCY_SCALE) / CURRENCY_SCALE;
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const packPalettes = useMemo(
    () => [
      { bg: 'linear-gradient(145deg, rgba(59,130,246,0.22), rgba(14,116,144,0.28))', progress: theme.palette.info.main },
      { bg: 'linear-gradient(145deg, rgba(168,85,247,0.22), rgba(126,34,206,0.28))', progress: theme.palette.secondary.main },
      { bg: 'linear-gradient(145deg, rgba(16,185,129,0.22), rgba(5,150,105,0.28))', progress: theme.palette.success.main },
      { bg: 'linear-gradient(145deg, rgba(249,115,22,0.22), rgba(217,119,6,0.28))', progress: theme.palette.warning.main },
      { bg: 'linear-gradient(145deg, rgba(236,72,153,0.22), rgba(219,39,119,0.28))', progress: theme.palette.error.main },
    ],
    [theme.palette]
  );
  const resolvedLocale = useMemo(() => {
    if (i18n.language?.startsWith('ar')) return 'ar-TN';
    if (i18n.language?.startsWith('fr')) return 'fr-FR';
    return 'en-US';
  }, [i18n.language]);
  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(resolvedLocale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    [resolvedLocale]
  );
  const formatPrice = useCallback((price) => numberFormatter.format(Number(price) || 0), [numberFormatter]);

  const refreshUserBalance = useCallback(() => {
    if (typeof refreshUser === 'function') {
      refreshUser();
    }
  }, [refreshUser]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const modeParam = params.get('mode');
    const normalizedMode = isGuest || modeParam === 'observer' ? 'observer' : 'participant';
    setJoinMode((prev) => (prev === normalizedMode ? prev : normalizedMode));
  }, [isGuest, location.search]);

  useEffect(() => {
    if (!token || !roomId) {
      setParticipationStatusPending(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const fetchParticipationStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auction-rooms/${roomId}/participation-status`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        if (cancelled) return;

        if (response.ok) {
          const payload = await response.json();
          const alreadyPaid = Boolean(payload?.data?.alreadyPaid);
          const amount = Number(payload?.data?.amount || 0);

          setParticipationConfirmed(alreadyPaid);
          setParticipationFeeReceipt(amount);

          if (alreadyPaid) {
            setShowParticipationConfirm(false);
          }
        }
      } catch (statusError) {
        if (statusError.name === 'AbortError') {
          return;
        }
        console.error('Failed to fetch participation status:', statusError);
      } finally {
        if (!cancelled) {
          setParticipationStatusPending(false);
        }
      }
    };

    fetchParticipationStatus();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [roomId, token]);

  useEffect(() => {
    if (!canBid) {
      setNeedPack(null);
      setPackStatus((prev) => ({
        ...prev,
        packs: [],
        totalPacks: 0,
        totalBidsGranted: 0,
        totalBidsUsed: 0,
        bidsLeftInCurrent: 0,
        bidsLostValue: 0,
      }));
    }
  }, [canBid]);

  useEffect(() => {
    if (joinMode !== 'participant') {
      setParticipationConfirmed(false);
      setShowParticipationConfirm(false);
    }
  }, [joinMode]);

  const packCardItems = useMemo(() => {
    const pricingList = Array.isArray(packPricing.pricing) ? packPricing.pricing : [];
    const packList = Array.isArray(packStatus.packs) ? packStatus.packs : [];
    const baseIndices = pricingList.length ? pricingList.map((item) => item.packIndex) : packList.map((p) => p.packIndex);
    const indices = Array.from(new Set([...baseIndices, 1, 2, 3, 4, 5]))
      .filter((index) => Number.isFinite(index) && index > 0 && index <= 5)
      .sort((a, b) => a - b);
    const grantFallback = packStatus.increment || packPricing.increment || 10;

    return indices.map((idx) => {
      const purchased = packList.find((p) => p.packIndex === idx);
      const pricing = pricingList.find((p) => p.packIndex === idx);
      const priceTokens = purchased?.pricePaid ?? pricing?.price ?? 0;
      const grantRaw = purchased?.grantSize ?? pricing?.grant ?? auctionRoom?.packConfigId?.increment ?? grantFallback;
      const grant = grantRaw === Infinity ? Number.MAX_SAFE_INTEGER : grantRaw;
      const used = purchased?.bidsUsed ?? 0;
      const percentUsed = grant > 0 ? Math.min(100, Math.round((used / grant) * 100)) : 0;
      const bidsLeft = grantRaw === Infinity ? '∞' : Math.max(0, grant - used);
      let purchasable = idx === 1;
      if (idx > 1) {
        const previous = packList.find((p) => p.packIndex === idx - 1);
        if (previous) {
          const prevGrant = previous.grantSize ?? grantFallback;
          const exhausted = previous.status === 'exhausted' || (previous.bidsUsed || 0) >= prevGrant;
          purchasable = exhausted;
        } else {
          purchasable = false;
        }
      }
      const isCurrent = Boolean(purchased) && purchased.packIndex === packStatus.currentPackIndex;

      return {
        idx,
        purchased,
        pricing,
        priceTokens,
        grantRaw,
        grant,
        used,
        percentUsed,
        bidsLeft,
        purchasable,
        isCurrent,
      };
    });
  }, [auctionRoom?.packConfigId, packPricing.increment, packPricing.pricing, packStatus.currentPackIndex, packStatus.increment, packStatus.packs]);
  const packGrantSummary = useMemo(() => {
    const grants = (packPricing.pricing || [])
      .map((p) => p.grant)
      .filter((value) => Number.isFinite(value) && value > 0);
    const unique = Array.from(new Set(grants)).sort((a, b) => a - b);
    if (unique.length === 1) {
      return t('liveAuction.packs.summary.uniform', { count: unique[0] });
    }
    if (unique.length > 1) {
      return t('liveAuction.packs.summary.variable');
    }
    const fallback = packStatus.increment || packPricing.increment || 10;
    return t('liveAuction.packs.summary.fallback', { count: fallback });
  }, [packPricing.increment, packPricing.pricing, packStatus.increment, t]);
  const participationFee = useMemo(() => {
    const candidates = [
      auctionRoom?.participationFee,
      auctionRoom?.productId?.participationFee,
      auctionRoom?.packConfigId?.participationFee,
    ];

    for (const fee of candidates) {
      const numeric = Number(fee);
      if (Number.isFinite(numeric) && numeric >= 0) {
        return numeric;
      }
    }

    return 0;
  }, [auctionRoom?.packConfigId?.participationFee, auctionRoom?.participationFee, auctionRoom?.productId?.participationFee]);

  const walletBalance = useMemo(() => {
    const candidateBalances = [user?.tokenBalance, user?.wallet?.tokenBalance]
      .map((value) => Number(value))
      .filter((num) => Number.isFinite(num) && num >= 0);

    if (candidateBalances.length === 0) {
      return 0;
    }

    return Math.max(...candidateBalances);
  }, [user?.tokenBalance, user?.wallet?.tokenBalance]);

  const ecommercePrice = useMemo(() => {
    const candidates = [auctionRoom?.productId?.price, auctionRoom?.price];
    for (const value of candidates) {
      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric >= 0) {
        return numeric;
      }
    }
    return null;
  }, [auctionRoom?.price, auctionRoom?.productId?.price]);

  // Persist the last known ecommerce price to avoid flicker when socket emits
  // intermediate room objects that might not include the product price.
  const lastKnownEcommercePriceRef = useRef(ecommercePrice);
  useEffect(() => {
    if (typeof ecommercePrice === 'number') {
      lastKnownEcommercePriceRef.current = ecommercePrice;
    }
  }, [ecommercePrice]);

  const displayedEcommercePrice = lastKnownEcommercePriceRef.current;

  const insufficientFundsForParticipation = participationFee > walletBalance;
  
  // Lock body scroll when winner modal visible
  useEffect(() => {
    if (showWinnerModal) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [showWinnerModal]);
  
  // Refs
  const countdownRef = useRef(null);
  const countdownTotalMsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const lastPingSentRef = useRef(null);
  const highestBidRequestRef = useRef(false);

  // REST fallback for pack status
  const fetchPackStatus = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auction-rooms/${roomId}/packs/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setPackStatus(data.data || packStatus);
    } catch (e) {
      console.error('Error fetching pack status:', e);
    }
  };

  // Fetch pack pricing (list all pack tiers)
  const fetchPackPricing = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auction-rooms/${roomId}/packs/pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setPackPricing(data.data || { pricing: [], increment: null });
    } catch (e) {
      console.error('Error fetching pack pricing:', e);
    }
  };

  // Fetch full room data with populated config and user stats
  const fetchFullRoomData = async () => {
    if (!roomId) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auction-rooms/${roomId}?populate=packConfigId,userBiddingStats`, { headers });
      if (!response.ok) {
        throw new Error(`Room request failed with status ${response.status}`);
      }
      const { data: fullRoom } = await response.json();
      if (fullRoom) {
        setAuctionRoom(fullRoom);
        setBidHistory(Array.isArray(fullRoom.bids) ? fullRoom.bids : []);
        // Extract user bidCount
        const userStats = token
          ? fullRoom.userBiddingStats?.find(stat => stat.userId === user?._id)
          : null;
        setUserBidCount(userStats?.bidCount || 0);
      }
    } catch (err) {
      console.error('Failed to load full room data:', err);
      setError(t('liveAuction.errors.roomLoadFailed'));
    }
  };

  const fetchLatestHighestBid = useCallback(async () => {
    if (!roomId || highestBidRequestRef.current) {
      return;
    }
    highestBidRequestRef.current = true;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auction-rooms/${roomId}`, { headers });
      const data = await res.json();
      if (data?.success && data.data) {
        const { currentHighestBid, analytics } = data.data;
        setAuctionRoom((prev) => {
          if (!prev) {
            return data.data;
          }
          return {
            ...prev,
            status: data.data.status ?? prev.status,
            startTime: data.data.startTime ?? prev.startTime,
            endTime: data.data.endTime ?? prev.endTime,
            productId: data.data.productId || prev.productId,
            currentHighestBid,
            analytics: analytics || prev.analytics,
          };
        });
        if (Array.isArray(data.data.bids)) {
          setBidHistory(data.data.bids);
        }
      }
    } catch (error) {
      // Suppress noisy logs; polling runs frequently
    } finally {
      highestBidRequestRef.current = false;
    }
  }, [roomId, token]);

  useEffect(() => {
    if (!roomId) return;
    fetchFullRoomData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, token]);

  useEffect(() => {
    if (!roomId) {
      return () => {};
    }

    // Guests receive a read-only live view through public REST polling.
    fetchLatestHighestBid();
    const pollInterval = setInterval(fetchLatestHighestBid, token ? 15000 : 5000);

    return () => {
      clearInterval(pollInterval);
      highestBidRequestRef.current = false;
    };
  }, [roomId, token, fetchLatestHighestBid]);
  // Re-compute packs when config or bidCount changes
  // Ensure full room data is loaded (with config) after socket join
  useEffect(() => {
    if (socketStatus === 'authenticated' && auctionRoom && !auctionRoom.packConfigId) {
      fetchFullRoomData();  // Fetch populated if socket data incomplete
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketStatus, auctionRoom]);

  // Fetch bid history
  const fetchBidHistory = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/bids/auction/${roomId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setBidHistory(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching bid history:', error);
    }
  };

  // Initialize socket connection
  useEffect(() => {
    if (!token) {
      setSocketStatus('guest');
      setError(null);
      return () => {};
    }

    // Connect and authenticate socket
    socketClient.connect(token);
    
    // Listen for socket status changes
    socketClient.on('socket_connected', () => {
      setSocketStatus('connected');
      setError(null);
    });

    socketClient.on('socket_authenticated', () => {
      setSocketStatus('authenticated');
    });

    socketClient.on('socket_disconnected', () => {
      setSocketStatus('disconnected');
      setError(t('liveAuction.errors.connectionLost'));
    });

    socketClient.on('socket_connect_error', () => {
      setSocketStatus('error');
      setError(t('liveAuction.errors.connectionFailed'));
    });

    socketClient.on('socket_auth_error', (data) => {
      setSocketStatus('error');
      setError(t('liveAuction.errors.authFailed', { message: data.message }));
    });

    socketClient.on('validation_error', (data) => {
      setError(data.message);
      // If it's a room joining error, reset socket status
      if (data.message.includes('room')) {
        setSocketStatus('authenticated');
      }
    });

    if (socketClient.isAuthenticated) {
      setSocketStatus('authenticated');
    }

    // Cleanup on unmount
    return () => {
      socketClient.leaveRoom(roomId, joinMode);
      socketClient.off('socket_connected');
      socketClient.off('socket_authenticated');
      socketClient.off('socket_disconnected');
      socketClient.off('socket_connect_error');
      socketClient.off('socket_auth_error');
      socketClient.off('validation_error');
    };
  }, [token, roomId, joinMode, t]);

  useEffect(() => {
    if (socketStatus !== 'authenticated' || !roomId) {
      return;
    }

    const desiredMode = joinMode === 'observer' ? 'observer' : 'participant';

    if (desiredMode === 'participant') {
      if (participationStatusPending) {
        return;
      }

      if (!participationConfirmed) {
        if (!showParticipationConfirm) {
          setShowParticipationConfirm(true);
        }
        return;
      }
    }

    if (!socketClient.isAuthenticated) {
      return;
    }

    const alreadyInRoom = socketClient.currentRoom === roomId;
    const sameMode = socketClient.currentMode === desiredMode;

    if (alreadyInRoom && sameMode) {
      return;
    }

    if (alreadyInRoom && !sameMode) {
      socketClient.leaveRoom(roomId, socketClient.currentMode);
    } else if (socketClient.currentRoom && socketClient.currentRoom !== roomId) {
      socketClient.leaveRoom(socketClient.currentRoom, socketClient.currentMode);
    }

    socketClient.joinRoom(roomId, desiredMode);
  }, [socketStatus, joinMode, participationConfirmed, participationStatusPending, roomId, showParticipationConfirm]);

  // Listen for auction room events
  useEffect(() => {
    if (socketStatus !== 'authenticated') return;

    // Helpers: normalize and dedupe participants by id
    const normalizeParticipant = (p) => {
      if (!p) return null;
      const id = p?.id || p?._id;
      if (!id) return null;
      const firstName = p?.profile?.firstName ?? p?.firstName ?? null;
      const lastName = p?.profile?.lastName ?? p?.lastName ?? null;
      return {
        id,
        _id: id,
        username: p?.username,
        profile: {
          firstName,
          lastName,
        },
        firstName,
        lastName,
      };
    };
    const dedupeById = (arr) => {
      const map = new Map();
      for (const item of arr) {
        if (!item) continue;
        const normalized = normalizeParticipant(item);
        if (normalized?.id) {
          map.set(normalized.id, normalized);
        }
      }
      return Array.from(map.values());
    };

    // Room joined event
    socketClient.on('room_joined', (data) => {
      setAuctionRoom(data.room);

      const normalizedMode = data.mode === 'observer' ? 'observer' : 'participant';
      setJoinMode((prev) => (prev === normalizedMode ? prev : normalizedMode));

      const normalized = dedupeById(data.room.participants || []);
      setParticipants(normalized);
      setParticipantCount(normalized.length || 0);

      const observersLength = Array.isArray(data.room.observers) ? data.room.observers.length : 0;
      setObserverCount(observersLength);

      setError(null);
      const joinMessage = normalizedMode === 'observer'
        ? t('liveAuction.notifications.observerMode')
        : t('liveAuction.notifications.joined');
      setSuccess(joinMessage);

      // Extract user bidCount from room stats if available
      const userStats = data.room.userBiddingStats?.find((stat) => stat.userId === user?._id);
      setUserBidCount(userStats?.bidCount || 0);

      if (normalizedMode === 'participant') {
        if (data.participationFeePaid || Number(data.participationFee) <= 0) {
          const feeAmount = Number(data.participationFee) || 0;
          setParticipationFeeReceipt(feeAmount);
          setParticipationConfirmed(true);
          setShowParticipationConfirm(false);
        }

        if (Number.isFinite(Number(data.participationFee)) && Number(data.participationFee) > 0) {
          refreshUserBalance();
        }
      }

      // Fetch bid history and fallback pack status
      fetchBidHistory();
      if (normalizedMode === 'participant') {
        fetchPackStatus();
      }
      fetchPackPricing();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    });

    // Bid events
    socketClient.on('bid_placed', (data) => {
      const message = t('liveAuction.notifications.bidPlaced');
      
      setSuccess(message);
      setIsPlacingBid(false);
      
      // Update user bidCount on successful bid
      setUserBidCount(prev => prev + 1);
      
  // Pack status will arrive via pack_status event
      
      // Refresh user wallet balance
      refreshUser();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    });

    socketClient.on('bid_error', (data) => {
      if (data.code === 'NEED_PACK') {
        setNeedPack({ packIndex: data.packIndex, price: data.price });
        setError(null);
      } else {
        setError(data.message);
      }
      setIsPlacingBid(false);
      
      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    });

    socketClient.on('bid_update', (data) => {
      setAuctionRoom(prev => ({
        ...prev,
        currentHighestBid: data.auctionRoom.currentHighestBid,
        endTime: data.auctionRoom.endTime,
        analytics: {
          ...(prev?.analytics || {}),
          ...(data.auctionRoom.analytics || {})
        }
      }));
      
    });

    socketClient.on('bid_history_update', (data) => {
      // Dedupe bids by _id to avoid duplicate keys if server re-sends overlapping windows
      try {
        const map = new Map();
        for (const b of data.bids || []) {
          if (!b?._id) continue;
          map.set(b._id, b);
        }
        setBidHistory(Array.from(map.values()));
      } catch {
        setBidHistory(data.bids || []);
      }
    });

    // Pack status updates
    socketClient.on('pack_status', (status) => {
      if (status) {
        setPackStatus(status);
        // Update user bidCount if available in status
        if (status.totalBidsUsed !== undefined) {
          setUserBidCount(status.totalBidsUsed);  // Assuming totalBidsUsed is user's
        }
      }
    });

    // Participant events
    socketClient.on('participant_joined', (data) => {
      const user = normalizeParticipant(data.user);
      setParticipants(prev => {
        const next = [...prev, user];
        return dedupeById(next);
      });
      setParticipantCount(data.participantCount);
    });

    socketClient.on('participant_left', (data) => {
      setParticipants(prev => prev.filter(p => (p.id || p._id) !== data.user.id));
      setParticipantCount(data.participantCount);
    });

    socketClient.on('participant_count_update', (data) => {
      setParticipantCount(data.count);
    });

    socketClient.on('observer_count_update', (data) => {
      setObserverCount(data?.count ?? 0);
    });

    // Auction events
    socketClient.on('auction_extended', (data) => {
      setAuctionRoom(prev => ({
        ...prev,
        endTime: data.newEndTime
      }));
      const formattedTime = new Date(data.newEndTime).toLocaleTimeString(resolvedLocale);
      setSuccess(t('liveAuction.notifications.auctionExtended', { time: formattedTime }));
      setTimeout(() => setSuccess(null), 5000);
    });

    socketClient.on('auction_ended', (data) => {
      setSuccess(t('liveAuction.notifications.auctionEnded'));
      
      // Always show winner modal if there's a winner
      if (data.winner) {
        setAuctionWinner(data.winner);
        setShowWinnerModal(true);
        
        // Check if current user is the winner (multiple ways to verify)
        const isCurrentUserWinner = 
          (data.winner.userId && user?._id && data.winner.userId.toString() === user._id.toString()) ||
          (data.winner.username && user?.username && data.winner.username === user.username);
        
        if (isCurrentUserWinner) {
          setShowConfetti(true);
      setSuccess(t('liveAuction.notifications.youWon'));
          // Keep confetti visible until user closes the modal
        }
      } else {
        // No winner case
    setSuccess(t('liveAuction.notifications.noWinner'));
      }
      
      // No automatic redirect; user will close the modal when ready
    });

    // Outbid notification
    socketClient.on('outbid_notification', (data) => {
      const formattedAmount = typeof data?.newHighestBid === 'number' ? formatPrice(data.newHighestBid) : null;
      setError(
        formattedAmount
          ? t('liveAuction.errors.outbidWithAmount', { amount: formattedAmount })
          : t('liveAuction.errors.outbid')
      );
      setTimeout(() => setError(null), 5000);
    });

    // Validation errors
    socketClient.on('validation_error', (data) => {
      setError(data.message);
      
      // If error is about room not being active, redirect to rooms page
      if (data.message && data.message.toLowerCase().includes('not active')) {
        setTimeout(() => {
          navigate('/auction-rooms');
        }, 3000);
      } else {
        setTimeout(() => setError(null), 5000);
      }
    });

    // Cleanup event listeners
    return () => {
      socketClient.off('room_joined');
      socketClient.off('bid_placed');
      socketClient.off('bid_error');
      socketClient.off('bid_update');
      socketClient.off('bid_history_update');
      socketClient.off('pack_status');
      socketClient.off('participant_joined');
      socketClient.off('participant_left');
      socketClient.off('participant_count_update');
      socketClient.off('observer_count_update');
      socketClient.off('auction_extended');
      socketClient.off('auction_ended');
      socketClient.off('outbid_notification');
      socketClient.off('validation_error');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketStatus, roomId, navigate, formatPrice, resolvedLocale, t]);

  const countdownTargetTime = auctionRoom?.status === 'pending'
    ? auctionRoom?.startTime
    : auctionRoom?.endTime;

  // Countdown timer
  useEffect(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    if (!countdownTargetTime) {
      setTimeRemaining(null);
      setCountdownProgress(0);
      countdownTotalMsRef.current = null;
      return;
    }

    const establishBaseline = () => {
      const now = Date.now();
      const endTime = new Date(countdownTargetTime).getTime();
      const timeLeft = Math.max(endTime - now, 0);

      countdownTotalMsRef.current = timeLeft > 0 ? timeLeft : 1;

      if (timeLeft <= 0) {
        setTimeRemaining(TIME_ENDED);
        setCountdownProgress(0);
        return false;
      }

      setCountdownProgress(100);
      return true;
    };

    if (!establishBaseline()) {
      return;
    }

    const updateCountdown = () => {
      const now = Date.now();
      const endTime = new Date(countdownTargetTime).getTime();
      const timeLeft = endTime - now;

      if (timeLeft <= 0) {
        setTimeRemaining(TIME_ENDED);
        setCountdownProgress(0);
        clearInterval(countdownRef.current);
        return;
      }

      if (countdownTotalMsRef.current && timeLeft > countdownTotalMsRef.current) {
        countdownTotalMsRef.current = timeLeft;
        setCountdownProgress(100);
      }

      const totalMs = countdownTotalMsRef.current || timeLeft || 1;
      const progress = Math.max(0, Math.min(100, (timeLeft / totalMs) * 100));
      setCountdownProgress(progress);

      const totalSeconds = Math.floor(timeLeft / 1000);
      const totalMinutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      if (auctionRoom?.status === 'pending') {
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);

        if (days > 0) {
          setTimeRemaining(t('liveAuction.countdown.daysHours', { days, hours }));
        } else if (hours > 0) {
          setTimeRemaining(t('liveAuction.countdown.hoursMinutes', { hours, minutes }));
        } else {
          setTimeRemaining(t('liveAuction.countdown.minutesSeconds', { minutes, seconds }));
        }
        return;
      }

      setTimeRemaining(`${totalMinutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };

    updateCountdown();
    countdownRef.current = setInterval(updateCountdown, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [auctionRoom?.status, countdownTargetTime, t]);

  useEffect(() => {
    const handlePingResponse = (data) => {
      const now = Date.now();
      const clientSentAt = typeof data?.clientSentAt === 'number' ? data.clientSentAt : lastPingSentRef.current;
      let measured = typeof data?.latency === 'number' ? data.latency : null;

      if (measured === null && typeof clientSentAt === 'number') {
        measured = now - clientSentAt;
      }

      if (measured !== null && Number.isFinite(measured)) {
        setLatency(Math.max(0, Math.round(measured)));
      }
    };

    socketClient.on('ping_response', handlePingResponse);

    return () => {
      socketClient.off('ping_response', handlePingResponse);
    };
  }, []);

  useEffect(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    if (socketStatus !== 'authenticated') {
      setLatency(null);
      return;
    }

    const sendPing = () => {
      if (typeof socketClient.sendPing !== 'function') return;
      const sentAt = socketClient.sendPing();
      if (sentAt) {
        lastPingSentRef.current = sentAt;
      }
    };

    sendPing();
    pingIntervalRef.current = setInterval(sendPing, 5000);

    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [socketStatus, refreshUserBalance]);

  // Handle connection refresh
  const handleConnectionRefresh = () => {
    if (isGuest) {
      fetchLatestHighestBid();
      setSuccess(t('liveAuction.guest.refreshed'));
      setTimeout(() => setSuccess(null), 2500);
      return;
    }

    setSocketStatus('disconnected');
    setError(t('liveAuction.alerts.reconnecting'));
    
    // Disconnect and reconnect socket
    socketClient.disconnect();
    setTimeout(() => {
      if (token) {
        socketClient.connect(token);
      }
    }, 1000);
  };

  const attemptBidPlacement = (amount) => {
    if (!canBid) {
      setError(t('liveAuction.errors.observerCannotBid'));
      setTimeout(() => setError(null), 5000);
      return false;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      setError(t('liveAuction.errors.cannotCalculateNextBid'));
      return false;
    }

    if (hasBidHistory && isUserCurrentLeader) {
      setError(t('liveAuction.errors.alreadyLeading'));
      return false;
    }

  setIsPlacingBid(true);
  setError(null);

  const normalized = roundToCurrency(amount);
  const payloadAmount = Math.max(normalized, amount);

  socketClient.placeBid(payloadAmount);

    return true;
  };

  const handleQuickBid = () => {
    if (!hasNextBid || !canBid) return;
    attemptBidPlacement(nextBidAmount);
  };

  // Purchase pack
  const handlePurchasePack = async () => {
    if (!canBid) {
      setError(t('liveAuction.errors.observerCannotBuyPacks'));
      setTimeout(() => setError(null), 5000);
      return;
    }

    if (!needPack || isPurchasingPack) return;
    setIsPurchasingPack(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auction-rooms/${roomId}/packs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ packIndex: needPack.packIndex })
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(t('liveAuction.success.packPurchased', { index: needPack.packIndex }));
        setNeedPack(null);
        fetchPackStatus();
        fetchPackPricing();
        if (autoPurchaseNext) {
          // attempt original bid automatically? user can re-click bid
        }
      } else {
        if (data.code === 'INSUFFICIENT_FUNDS' || res.status === 402) {
          setError(t('liveAuction.errors.insufficientFundsRedirect'));
          // Redirect user to payment / top-up page; pass return path
          setTimeout(() => {
            // Assume a generic payment route exists (/payment) without product context for wallet top-up
            navigate(`/payment?topup=1&return=/rooms/${roomId}`);
          }, 1500);
        } else {
          setError(data.message || t('liveAuction.errors.packPurchaseFailed'));
        }
      }
    } catch (err) {
      setError(t('liveAuction.errors.packPurchaseFailed'));
    } finally {
      setIsPurchasingPack(false);
    }
  };
  const handleConfirmParticipation = useCallback(() => {
    if (insufficientFundsForParticipation) {
      return;
    }

    setParticipationConfirmed(true);
    setShowParticipationConfirm(false);
    setJoinMode('participant');

    const params = new URLSearchParams(location.search);
    params.set('mode', 'participant');
    const newSearch = params.toString();
    navigate(newSearch ? `${location.pathname}?${newSearch}` : location.pathname, { replace: true });

    if (socketClient.isAuthenticated) {
      socketClient.joinRoom(roomId, 'participant');
    }
  }, [insufficientFundsForParticipation, location.pathname, location.search, navigate, roomId]);

  const handleParticipationDecline = useCallback(() => {
    setParticipationConfirmed(false);
    setShowParticipationConfirm(false);
    setJoinMode('observer');

    const params = new URLSearchParams(location.search);
    params.set('mode', 'observer');
    const newSearch = params.toString();
    navigate(newSearch ? `${location.pathname}?${newSearch}` : location.pathname, { replace: true });

    if (socketClient.isAuthenticated) {
      socketClient.joinRoom(roomId, 'observer');
    }
  }, [location.pathname, location.search, navigate, roomId]);

  // Get minimum bid amount
  const getNextBidAmount = useCallback(() => {
    if (!auctionRoom) return 0;

    const increment = Number(auctionRoom?.bidIncrement ?? QUICK_BID_INCREMENT);
    const currentBid = Number(auctionRoom?.currentHighestBid?.amount);

    if (Number.isFinite(currentBid)) {
      return currentBid + increment;
    }

    const baseline = Number(auctionRoom?.minBid ?? increment);
    return Number.isFinite(baseline) ? baseline : increment;
  }, [auctionRoom, QUICK_BID_INCREMENT]);
  const handleCloseError = (_, reason) => {
    if (reason === 'clickaway') return;
    setError(null);
  };
  const handleCloseSuccess = (_, reason) => {
    if (reason === 'clickaway') return;
    setSuccess(null);
  };
  const connectionLabel = isGuest
    ? t('liveAuction.connection.labels.guest')
    : socketStatus === 'authenticated'
    ? t('liveAuction.connection.labels.connected')
    : socketStatus === 'connected'
      ? t('liveAuction.connection.labels.connecting')
      : t('liveAuction.connection.labels.disconnected');
  const connectionColor = isGuest ? 'info' : socketStatus === 'authenticated' ? 'success' : socketStatus === 'connected' ? 'warning' : 'error';
  const connectionIcon = isGuest
    ? <VisibilityIcon fontSize="small" />
    : socketStatus === 'authenticated'
    ? <CheckCircleIcon fontSize="small" />
    : socketStatus === 'connected'
      ? <AccessTimeIcon fontSize="small" />
      : <ErrorOutlineIcon fontSize="small" />;
  const countdownColor = useMemo(() => {
    if (countdownProgress > 60) return theme.palette.success.light;
    if (countdownProgress > 30) return theme.palette.warning.light;
    return theme.palette.error.light;
  }, [countdownProgress, theme.palette.error.light, theme.palette.success.light, theme.palette.warning.light]);
  const countdownStrokeGradient = useMemo(() => {
    const degrees = Math.max(0, Math.min(360, countdownProgress * 3.6));
    return `conic-gradient(from 90deg, ${countdownColor} ${degrees}deg, rgba(148,163,184,0.25) ${degrees}deg 360deg)`;
  }, [countdownColor, countdownProgress]);
  const nextBidAmount = useMemo(() => getNextBidAmount(), [getNextBidAmount]);
  const nextBidDisplayAmount = useMemo(() => roundToCurrency(nextBidAmount), [nextBidAmount, roundToCurrency]);
  const hasNextBid = Number.isFinite(nextBidAmount) && nextBidAmount > 0;
  const formattedNextBid = useMemo(
    () => (hasNextBid ? t('liveAuction.common.amountWithCurrency', { amount: formatPrice(nextBidDisplayAmount) }) : null),
    [formatPrice, hasNextBid, nextBidDisplayAmount, t]
  );
  const hasBidHistory = Boolean(bidHistory && bidHistory.length > 0);
  const isUserCurrentLeader = auctionRoom?.currentHighestBid?.userId === user?._id;
  const quickBidDisabled = !canBid || !hasNextBid || isPlacingBid || socketStatus !== 'authenticated' || (hasBidHistory && isUserCurrentLeader);
  const quickBidTooltip = isGuest
    ? t('liveAuction.guest.signInTooltip')
    : !canBid
    ? t('liveAuction.quickBid.tooltip.observer')
    : !hasNextBid
      ? t('liveAuction.quickBid.tooltip.waiting')
      : hasBidHistory && isUserCurrentLeader
        ? t('liveAuction.quickBid.tooltip.leading')
        : t('liveAuction.quickBid.tooltip.default');
  const pingDisplay = latency != null ? `${latency} ms` : '—';
  const pingColor = useMemo(() => {
    if (latency == null) return theme.palette.text.secondary;
    if (latency <= 80) return theme.palette.success.light;
    if (latency <= 140) return theme.palette.warning.light;
    return theme.palette.error.light;
  }, [latency, theme.palette.error.light, theme.palette.success.light, theme.palette.text.secondary, theme.palette.warning.light]);

  if (!auctionRoom) {
    return (
      <SciFiBackground>
        <Box minHeight="100vh" display="flex" flexDirection="column">
          <Navbar />
          <Container maxWidth="md" sx={{ flex: 1, py: { xs: 6, md: 10 } }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                p: { xs: 4, md: 6 },
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'divider',
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(17, 24, 39, 0.92))'
                    : 'linear-gradient(135deg, #ffffff, #eff4ff)',
              }}
            >
              <Stack spacing={3} alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <FlashOnIcon />
                </Avatar>
                <CircularProgress color="primary" size={48} thickness={4} />
                <Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {t('liveAuction.loading.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('liveAuction.loading.description')}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Container>
        </Box>
      </SciFiBackground>
    );
  }

  return (
    <SciFiBackground>
      <Box minHeight="100vh" position="relative">
        <Navbar />

        <Box position="fixed" top={{ xs: 88, md: 96 }} right={{ xs: 16, md: 32 }} zIndex={1300}>
          <Tooltip title={isGuest ? t('liveAuction.guest.refreshTooltip') : t('liveAuction.connection.refreshTooltip')}>
            <Button
              variant="contained"
              color={connectionColor}
              startIcon={connectionIcon}
              onClick={handleConnectionRefresh}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                px: 2.5,
                py: 1,
                boxShadow: 6,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Typography fontWeight={600} sx={{ letterSpacing: 0.3 }}>
                  {connectionLabel}
                </Typography>
                {socketStatus === 'authenticated' && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: pingColor,
                      fontFamily: '"Roboto Mono", monospace',
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                    }}
                  >
                    {pingDisplay}
                  </Typography>
                )}
              </Stack>
            </Button>
          </Tooltip>
        </Box>

        <Snackbar
          open={Boolean(error)}
          autoHideDuration={6000}
          onClose={handleCloseError}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseError} severity="error" variant="filled" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar
          open={Boolean(success)}
          autoHideDuration={5000}
          onClose={handleCloseSuccess}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSuccess} severity="success" variant="filled" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 6, md: 8 }, px: { xs: 2, sm: 3, md: 4 } }}>
          {auctionRoom && (
            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="stretch">
              <Grid item xs={12}>
                    <Card
                      sx={{
                        borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'rgba(255,255,255,0.12)',
                    background: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(15,23,42,0.88), rgba(30,41,59,0.75))'
                        : 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(236,244,255,0.88))',
                    backdropFilter: 'blur(20px)',
                    boxShadow: 12,
                    height: '100%',
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, sm: 4, md: 6 } }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={{ xs: 2, sm: 4 }}
                      alignItems={{ xs: 'center', sm: 'center' }}
                      mb={{ xs: 3, sm: 4, md: 5 }}
                    >
                      <Box
                        sx={{
                          width: { xs: 120, sm: 120, md: 140 },
                          height: { xs: 120, sm: 120, md: 140 },
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'rgba(255,255,255,0.18)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'rgba(15,23,42,0.6)',
                          flexShrink: 0,
                        }}
                      >
                        {auctionRoom.productId?.images?.length ? (
                          <Box
                            component="img"
                            src={auctionRoom.productId.images[0].thumbnailUrl}
                            alt={auctionRoom.productId?.name}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <EmojiEventsIcon sx={{ fontSize: { xs: 42, md: 54 }, color: 'rgba(255,255,255,0.6)' }} />
                        )}
                      </Box>
                      {/* retail price moved next to mode chip for compact horizontal layout */}
                      <Box flex={1} minWidth={0}>
                        <Typography variant="overline" color="text.secondary" gutterBottom sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                          {auctionRoom.productId?.category || t('liveAuction.fallback.productCategory')}
                        </Typography>

                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          alignItems={{ xs: 'center', sm: 'center' }}
                          spacing={{ xs: 1.5, md: 3 }}
                          justifyContent="space-between"
                          sx={{ mt: { xs: 1, md: 1.5 }, gap: 2 }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant={{ xs: 'h5', sm: 'h4', md: 'h3' }}
                              fontWeight={700}
                                sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                flexGrow: 1,
                                minWidth: 0,
                                fontSize: { xs: '1.25rem', sm: '1.4rem', md: '2.2rem' },
                                textAlign: { xs: 'center', sm: 'left' },
                              }}
                            >
                              {auctionRoom.productId?.name || auctionRoom?.title || t('liveAuction.fallback.product')}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0, justifyContent: { xs: 'center', sm: 'flex-end' }, mt: { xs: 1, sm: 0 } }}>
                            {typeof displayedEcommercePrice === 'number' && (
                              <Typography
                                variant="h6"
                                color="text.primary"
                                fontWeight={800}
                                sx={{ fontSize: { xs: '0.95rem', sm: '1.2rem', md: '1.6rem' } }}
                              >
                                {formatPrice(displayedEcommercePrice)} TND
                              </Typography>
                            )}
                            <Chip
                              icon={isObserver ? <VisibilityIcon fontSize="small" /> : <GavelIcon fontSize="small" />}
                              label={isGuest
                                ? t('liveAuction.mode.guest')
                                : isObserver
                                  ? t('liveAuction.mode.observer')
                                  : t('liveAuction.mode.participant')}
                              color={isObserver ? 'info' : 'success'}
                              variant={isObserver ? 'filled' : 'outlined'}
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        </Stack>

                        {isGuest ? (
                          <Alert
                            severity="info"
                            sx={{ mt: 2, alignItems: 'center' }}
                            action={
                              <Button color="inherit" size="small" startIcon={<LoginIcon />} onClick={() => navigate('/auth')}>
                                {t('liveAuction.guest.signIn')}
                              </Button>
                            }
                          >
                            <Typography variant="subtitle2" fontWeight={700}>
                              {t('liveAuction.guest.title')}
                            </Typography>
                            <Typography variant="body2">
                              {t('liveAuction.guest.description')}
                            </Typography>
                          </Alert>
                        ) : isObserver ? (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            {t('liveAuction.observerNotice')}
                          </Alert>
                        ) : null}
                      </Box>
                    </Stack>

                    <Box mb={{ xs: 4, sm: 5 }}>
                      <Box
                        sx={{
                          borderRadius: 1,
                          background: countdownStrokeGradient,
                          p: { xs: '2px', sm: '3px' },
                          transition: 'background 0.4s ease',
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 1,
                            border: '1px solid rgba(255,255,255,0.12)',
                            background: (theme) =>
                              theme.palette.mode === 'dark' ? '#0f172a' : '#ffffff',
                            px: { xs: 2.5, sm: 3.5, md: 4 },
                            py: { xs: 3, sm: 3.5, md: 4 },
                          }}
                        >
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={{ xs: 3, md: 4 }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'stretch', md: 'center' }}
                          >
                            <Stack spacing={1.5} alignItems={{ xs: 'center', md: 'flex-start' }} sx={{ minWidth: { md: 240 } }}>
                              <Typography variant="overline" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                                {auctionRoom.status === 'pending'
                                  ? t('liveAuction.countdown.startsIn')
                                  : t('liveAuction.countdown.label')}
                              </Typography>
                              <Typography
                                variant={{ xs: 'h4', sm: 'h3', md: 'h2' }}
                                fontWeight={800}
                                color={countdownColor}
                                sx={{ fontFamily: '"Roboto Mono", monospace', letterSpacing: 3, textAlign: { xs: 'center', md: 'left' } }}
                              >
                                {timeRemaining === TIME_ENDED
                                  ? auctionRoom.status === 'pending'
                                    ? t('liveAuction.countdown.starting')
                                    : t('liveAuction.countdown.ended')
                                  : (timeRemaining || '—')}
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={Math.max(0, Math.min(100, countdownProgress))}
                                sx={{
                                  width: '100%',
                                  height: 8,
                                  borderRadius: 999,
                                  bgcolor: 'rgba(148,163,184,0.2)',
                                  '& .MuiLinearProgress-bar': {
                                    background: `linear-gradient(90deg, ${countdownColor}, rgba(94,234,212,0.75))`,
                                  },
                                }}
                              />
                            </Stack>

                            <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="overline" color="text.secondary">
                                {t('liveAuction.highestBid.label')}
                              </Typography>
                              <Typography
                                variant={{ xs: 'h3', sm: 'h2', md: 'h1' }}
                                fontWeight={800}
                                color="primary.light"
                                sx={{ lineHeight: 1, textAlign: 'center', fontSize: { xs: '2.2rem', sm: '3rem', md: '4.4rem' } }}
                              >
                                {auctionRoom.currentHighestBid
                                  ? formatPrice(auctionRoom.currentHighestBid.amount)
                                  : formatPrice(auctionRoom.minBid)}
                              </Typography>
                              <Typography variant="body1" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                                {isGuest
                                  ? t('liveAuction.guest.currentPriceCaption')
                                  : auctionRoom.currentHighestBid?.userId === user?._id
                                  ? t('liveAuction.highestBid.leader')
                                  : t('liveAuction.highestBid.trailing')}
                              </Typography>
                            </Stack>

                            <Stack spacing={2.25} alignItems={{ xs: 'center', md: 'flex-end' }} sx={{ minWidth: { md: 240 } }}>
                              <Tooltip title={quickBidTooltip} placement="top" arrow>
                                <Box component="span" sx={{ width: { xs: '100%', md: 'auto' } }}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={isGuest ? () => navigate('/auth') : handleQuickBid}
                                    disabled={!isGuest && quickBidDisabled}
                                    startIcon={isGuest ? <LoginIcon /> : undefined}
                                    sx={{
                                      borderRadius: 1,
                                      px: { xs: 2.5, sm: 3.75 },
                                      py: { xs: 1.2, sm: 1.35 },
                                      width: { xs: '100%', md: 'auto' },
                                      fontWeight: 700,
                                      fontSize: { xs: '1.05rem', sm: '1.25rem' },
                                    }}
                                  >
                                    {isGuest
                                      ? t('liveAuction.guest.signInToBid')
                                      : hasNextBid
                                        ? `+ ${formatPrice(nextBidDisplayAmount)} TND`
                                        : t('liveAuction.quickBid.button')}
                                  </Button>
                                </Box>
                              </Tooltip>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ maxWidth: 240, textAlign: { xs: 'center', md: 'right' } }}
                              >
                                {isGuest ? t('liveAuction.guest.bidCaption') : t('liveAuction.quickBid.caption')}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Paper>
                      </Box>
                    </Box>

                    {!isGuest && (
                    <Box mb={{ xs: 4, sm: 5, md: 6 }}>
                      <Stack direction="row" spacing={{ xs: 1, sm: 1.5, md: 1.5 }} alignItems="center" mb={{ xs: 2.5, sm: 3, md: 3 }}>
                        <AutoAwesomeIcon fontSize="small" color="primary" />
                        <Typography variant={{ xs: 'body1', sm: 'h6', md: 'h6' }} fontWeight={600}>
                          {t('liveAuction.packs.title')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {packGrantSummary}
                        </Typography>
                      </Stack>
                      {isMobile ? (
                        <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} flexWrap="wrap" justifyContent="center">
                          {packCardItems.map((item) => {
                            const palette = packPalettes[(item.idx - 1) % packPalettes.length];
                            const circleBg = item.isCurrent ? palette.bg : 'transparent';
                            const borderColor = item.isCurrent ? palette.progress : 'rgba(148,163,184,0.45)';
                            const bidsInfo = item.grantRaw === Infinity
                              ? t('liveAuction.packs.infiniteBids')
                              : t('liveAuction.packs.bidsCount', { count: item.grant });
                            const priceInfo = t('liveAuction.common.amountWithCurrency', { amount: formatPrice(item.priceTokens) });
                            const remainingLabel = item.bidsLeft === '∞'
                              ? t('liveAuction.packs.remainingInfinite')
                              : t('liveAuction.packs.remaining', { count: item.bidsLeft });
                            const circleClickable = item.purchasable && !item.purchased;

                            return (
                              <Box
                                key={`pack-mobile-${item.idx}`}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  textAlign: 'center',
                                  gap: 1,
                                  minWidth: { xs: 100, sm: 110 },
                                  px: { xs: 0.5, sm: 1 },
                                  py: 1,
                                }}
                              >
                                <Tooltip
                                  title={circleClickable
                                    ? t('liveAuction.packs.tooltips.purchase')
                                    : item.purchased
                                      ? t('liveAuction.packs.tooltips.owned')
                                      : t('liveAuction.packs.tooltips.locked')}
                                  placement="top"
                                  arrow
                                >
                                  <Box
                                    onClick={() => {
                                      if (circleClickable) {
                                        setNeedPack({ packIndex: item.idx, price: item.priceTokens });
                                      }
                                    }}
                                    sx={{
                                      width: { xs: 80, sm: 96 },
                                      height: { xs: 80, sm: 96 },
                                      borderRadius: '50%',
                                      border: `2px solid ${borderColor}`,
                                      background: circleBg,
                                      color: '#ffffff',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: circleClickable ? 'pointer' : 'default',
                                      transition: 'all 0.3s ease',
                                      boxShadow: item.isCurrent ? '0 10px 22px rgba(14,116,144,0.35)' : 'none',
                                      opacity: item.purchasable || item.purchased ? 1 : 0.5,
                                      px: 1,
                                      textTransform: 'none',
                                    }}
                                  >
                                    <Typography variant="subtitle2" fontWeight={700}>
                                      #{item.idx}
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.7rem' }}>
                                      {bidsInfo}
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.75, fontSize: '0.7rem' }}>
                                      {priceInfo}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                                {item.purchased ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                                    {remainingLabel}
                                  </Typography>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="text"
                                    color="secondary"
                                    disabled={!item.purchasable}
                                    onClick={() => item.purchasable && setNeedPack({ packIndex: item.idx, price: item.priceTokens })}
                                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.7rem', p: 0.5 }}
                                  >
                                    {item.purchasable ? t('liveAuction.packs.actions.buyShort') : t('liveAuction.packs.actions.locked')}
                                  </Button>
                                )}
                                {item.purchased?.status === 'exhausted' && (
                                  <Chip size="small" color="warning" label={t('liveAuction.packs.status.exhausted')} sx={{ opacity: 0.85 }} />
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Box sx={{ pb: 1, px: { xs: 1, sm: 0 } }}>
                          <Stack
                            direction="row"
                            spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
                            flexWrap="wrap"
                            justifyContent="center"
                            useFlexGap
                            minWidth={0}
                          >
                            {packCardItems.map((item) => {
                              const palette = packPalettes[(item.idx - 1) % packPalettes.length];
                              const bidsLabel = item.bidsLeft === '∞'
                                ? t('liveAuction.packs.remainingInfinite')
                                : t('liveAuction.packs.remaining', { count: item.bidsLeft });
                              const totalGrant = item.grantRaw === Infinity ? '∞' : item.grant;
                              return (
                                <Box
                                  key={`pack-${item.idx}`}
                                  sx={{
                                    flex: '0 1 210px',
                                    maxWidth: { xs: '100%', md: 240 },
                                    minWidth: { sm: 200 },
                                    display: 'flex',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Card
                                    sx={{
                                      height: '100%',
                                      borderRadius: 1,
                                      background: palette.bg,
                                      color: '#ffffff',
                                      border: '1px solid rgba(255,255,255,0.15)',
                                      position: 'relative',
                                      overflow: 'hidden',
                                      boxShadow: item.isCurrent ? '0 10px 18px rgba(15,118,110,0.32)' : 4,
                                      transform: item.isCurrent ? 'translateY(-4px)' : 'none',
                                      transition: 'all 0.3s ease',
                                    }}
                                  >
                                    <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 3 } }}>
                                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                                        <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1, opacity: 0.75 }}>
                                          {t('liveAuction.packs.cardLabel', { index: item.idx })}
                                        </Typography>
                                        {item.isCurrent && (
                                          <Chip
                                            size="small"
                                            color="success"
                                            icon={<FlashOnIcon fontSize="small" />}
                                            label={t('liveAuction.packs.status.current')}
                                            sx={{ bgcolor: 'rgba(0,0,0,0.35)', color: '#ffffff', fontWeight: 600 }}
                                          />
                                        )}
                                      </Stack>
                                      <Typography variant="h6" fontWeight={700} mb={0.5}>
                                        {t('liveAuction.common.amountWithCurrency', { amount: formatPrice(item.priceTokens) })}
                                      </Typography>
                                      <Box sx={{ mb: 2 }} />
                                      {item.purchased ? (
                                        <>
                                          <LinearProgress
                                            variant="determinate"
                                            value={Number(item.percentUsed) || 0}
                                            sx={{
                                              height: 6,
                                              borderRadius: 999,
                                              bgcolor: 'rgba(255,255,255,0.25)',
                                              '& .MuiLinearProgress-bar': { backgroundColor: palette.progress },
                                            }}
                                          />
                                          <Stack direction="row" justifyContent="space-between" mt={1.5}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                                              {t('liveAuction.packs.usageSummary', { used: item.used, total: totalGrant })}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{ color: item.bidsLeft === 0 ? 'warning.light' : 'success.light', fontWeight: 600 }}
                                            >
                                              {bidsLabel}
                                            </Typography>
                                          </Stack>
                                          {item.purchased.status === 'exhausted' && (
                                            <Chip
                                              size="small"
                                              color="warning"
                                              label={t('liveAuction.packs.status.exhausted')}
                                              sx={{ mt: 2, bgcolor: 'rgba(0,0,0,0.35)', color: '#ffffff' }}
                                            />
                                          )}
                                        </>
                                      ) : (
                                        <Button
                                          variant="contained"
                                          fullWidth
                                          size="small"
                                          color="secondary"
                                          disabled={!canBid || !item.purchasable}
                                          onClick={() => {
                                            if (!canBid || !item.purchasable) return;
                                            setNeedPack({ packIndex: item.idx, price: item.priceTokens });
                                          }}
                                          sx={{
                                            mt: 1.5,
                                            borderRadius: 1,
                                            bgcolor: item.purchasable ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.14)',
                                            color: '#ffffff',
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
                                            opacity: item.purchasable ? 1 : 0.5,
                                          }}
                                        >
                                          {item.purchasable
                                            ? t('liveAuction.packs.actions.buyFull')
                                            : t('liveAuction.packs.actions.waitForPrevious', { index: item.idx - 1 })}
                                        </Button>
                                      )}
                                    </CardContent>
                                  </Card>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: { xs: 2.5, sm: 3, md: 3.5 } }}>
                        {t('liveAuction.packs.footnote')}
                      </Typography>
                    </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Container>

        <Dialog
          open={!isGuest && showParticipationConfirm}
          onClose={handleParticipationDecline}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t('liveAuction.dialogs.participation.title')}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t('liveAuction.dialogs.participation.description')}
              </Typography>

              {auctionRoom ? (
                <Stack spacing={1.5}>
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">
                      {t('liveAuction.dialogs.participation.roomLabel')}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {auctionRoom?.productId?.name || auctionRoom?.title || t('liveAuction.fallback.room')}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('liveAuction.dialogs.participation.feeLabel')}
                    </Typography>
                    <Typography variant="h6" color={participationFee > 0 ? 'error.main' : 'success.main'} fontWeight={700}>
                      {t('liveAuction.common.amountWithCurrency', { amount: formatPrice(participationFee) })}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('liveAuction.dialogs.participation.walletBalance')}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {t('liveAuction.common.amountWithCurrency', { amount: formatPrice(walletBalance) })}
                    </Typography>
                  </Stack>

                  {insufficientFundsForParticipation && (
                    <Alert severity="warning" variant="outlined">
                      {t('liveAuction.dialogs.participation.insufficientFunds')}
                    </Alert>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('liveAuction.dialogs.participation.loading')}
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleParticipationDecline} color="inherit">
              {t('liveAuction.dialogs.participation.buttons.viewAsObserver')}
            </Button>
            <Button
              onClick={handleConfirmParticipation}
              variant="contained"
              color="primary"
              disabled={insufficientFundsForParticipation}
            >
              {participationFee > 0
                ? t('liveAuction.dialogs.participation.buttons.payAndJoin')
                : t('liveAuction.dialogs.participation.buttons.join')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!isGuest && Boolean(needPack)}
          onClose={() => !isPurchasingPack && setNeedPack(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t('liveAuction.dialogs.packPurchase.title', { index: needPack?.packIndex })}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t('liveAuction.dialogs.packPurchase.description')}
              </Typography>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {t('liveAuction.dialogs.packPurchase.priceLabel')}
                </Typography>
                <Typography variant="h6" color="primary" fontWeight={700}>
                  {t('liveAuction.common.amountWithCurrency', { amount: formatPrice(needPack?.price || 0) })}
                </Typography>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={autoPurchaseNext}
                    onChange={(event) => setAutoPurchaseNext(event.target.checked)}
                  />
                }
                label={t('liveAuction.dialogs.packPurchase.autoPurchase')}
                sx={{ alignItems: 'flex-start', mt: 1 }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNeedPack(null)} disabled={isPurchasingPack}>
              {t('liveAuction.dialogs.packPurchase.buttons.cancel')}
            </Button>
            <Button onClick={handlePurchasePack} variant="contained" disabled={isPurchasingPack}>
              {isPurchasingPack
                ? t('liveAuction.dialogs.packPurchase.buttons.processing')
                : t('liveAuction.dialogs.packPurchase.buttons.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {showConfetti && typeof window !== 'undefined' && (
          <Box position="fixed" top={0} left={0} right={0} bottom={0} zIndex={3000} pointerEvents="none">
            <Confetti
              width={window.innerWidth}
              height={window.innerHeight}
              recycle
              numberOfPieces={320}
              gravity={0.3}
            />
          </Box>
        )}

        <Dialog
          open={showWinnerModal && Boolean(auctionWinner)}
          onClose={() => {
            setShowWinnerModal(false);
            setShowConfetti(false);
          }}
          fullScreen
          PaperProps={{
            sx: {
              background: 'radial-gradient(circle at top, rgba(88,28,135,0.92), rgba(9,9,11,0.94))',
              color: '#ffffff',
            },
          }}
        >
          <Box
            sx={{
              maxWidth: 720,
              mx: 'auto',
              width: '100%',
              py: { xs: 6, md: 10 },
              px: { xs: 3, sm: 6 },
              textAlign: 'center',
            }}
          >
            <Stack spacing={4} alignItems="center">
              <Typography variant="h2" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <EmojiEventsIcon sx={{ fontSize: 56 }} />
                {t('liveAuction.winnerModal.title')}
              </Typography>
              <Typography variant="h5" color="text.secondary">
                {t('liveAuction.winnerModal.subtitle')}
              </Typography>
              {auctionRoom?.productId?.images?.[0] && (
                <Box
                  component="img"
                  src={auctionRoom.productId.images[0].thumbnailUrl}
                  alt={auctionRoom.productId?.name}
                  sx={{
                    width: { xs: 220, sm: 260, md: 300 },
                    height: { xs: 220, sm: 260, md: 300 },
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: '4px solid rgba(250,204,21,0.8)',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.35)',
                  }}
                />
              )}
              <Typography variant="h4" fontWeight={700} color="primary.light">
                {auctionRoom?.productId?.name}
              </Typography>
              {typeof auctionRoom?.productId?.price === 'number' && (
                <Typography variant="body1" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                  {t('liveAuction.winnerModal.retailPrice', { price: formatPrice(auctionRoom.productId.price) })} TND
                </Typography>
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="center" sx={{ width: '100%' }}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    p: 3,
                    borderRadius: 1,
                    background: 'rgba(59,130,246,0.12)',
                    border: '1px solid rgba(59,130,246,0.35)',
                  }}
                >
                  <Typography variant="overline" color="text.secondary">
                    {t('liveAuction.winnerModal.winnerLabel')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {auctionWinner?.username || `${auctionWinner?.firstName || ''} ${auctionWinner?.lastName || ''}`}
                  </Typography>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    p: 3,
                    borderRadius: 1,
                    background: 'rgba(16,185,129,0.12)',
                    border: '1px solid rgba(16,185,129,0.35)',
                  }}
                >
                  <Typography variant="overline" color="text.secondary">
                    {t('liveAuction.winnerModal.bidLabel')}
                  </Typography>
                  <Typography variant="h4" fontWeight={800} color="success.main">
                    {formatPrice(auctionWinner?.amount || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('liveAuction.common.currency')}
                  </Typography>
                </Paper>
              </Stack>
              {/* Buttons removed: congrats screen is informational with no time limit */}
            </Stack>
          </Box>
        </Dialog>
      </Box>
    </SciFiBackground>
  );
};

export default LiveAuctionRoom; 
